/**
 * The same values as `tokens.css`, as typed constants (spec M0-F7 §4).
 *
 * They exist because the board grid is a `<canvas>` and cannot read a CSS custom property per
 * cell, and because tests need to assert against numbers rather than strings. A unit test walks
 * `tokens.css` and asserts the two agree name-by-name (T1), so this file may never drift.
 */
/** Fixed dimensions, in CSS pixels (spec §7.6). */
export const dimensions = {
    topbarHeight: 48,
    sidebarRailWidth: 72,
    sidebarPaneWidth: 279,
    sidebarWidthCollapsed: 72,
    sidebarWidthExpanded: 351,
    sidebarPaneMinWidth: 240,
    sidebarPaneMaxWidth: 520,
    sidebarRowHeight: 36,
    /** P-037: 22 px per depth, and the width of the glyph column itself. */
    sidebarIndentStep: 22,
    rowHeight: 36,
    columnHeaderHeight: 32,
    groupHeaderHeight: 76,
    groupColorBarWidth: 6,
    /** P-024: monday's view-tab strip runs y 107 -> 141 on all ten parity boards. */
    viewTabsHeight: 34,
    /** P-024: the board title band, y 49 -> 107 under our 49 px top bar. */
    boardHeaderHeight: 58,
    controlHeightSmall: 32,
    /** P-022: a column with **no stored width** renders 140 px, whatever its type. */
    columnWidthDefault: 140,
    /** P-022: except `people`, measured at 98 (`174316936` `Executor`, `width: null`). */
    columnWidthPeopleDefault: 98,
    /** P-021: the name column's **sticky region**, not its cell — 698 leaves a 594 px cell. */
    columnWidthNameDefault: 698,
    columnWidthMin: 60,
    columnWidthMax: 1200,
    sidePanelWidth: 700,
    paneWidth: 600,
    menuWidth: 264,
    menuWidthGallery: 280,
    popoverWidth: 420,
    modalWidth: 620,
    bottomBarHeight: 66,
    avatarSizeSmall: 24,
    avatarSizeMedium: 32,
    avatarSizeLarge: 96,
    brandLogoSize: 40,
    topbarSearchWidth: 475,
};
/** The CSS custom-property name each dimension is emitted as. */
export const dimensionVars = {
    topbarHeight: '--topbar-height',
    sidebarRailWidth: '--sidebar-rail-width',
    sidebarPaneWidth: '--sidebar-pane-width',
    sidebarWidthCollapsed: '--sidebar-width-collapsed',
    sidebarWidthExpanded: '--sidebar-width-expanded',
    sidebarPaneMinWidth: '--sidebar-pane-min-width',
    sidebarPaneMaxWidth: '--sidebar-pane-max-width',
    sidebarRowHeight: '--sidebar-row-height',
    sidebarIndentStep: '--sidebar-indent-step',
    rowHeight: '--row-height',
    columnHeaderHeight: '--column-header-height',
    groupHeaderHeight: '--group-header-height',
    groupColorBarWidth: '--group-color-bar-width',
    viewTabsHeight: '--view-tabs-height',
    boardHeaderHeight: '--board-header-height',
    controlHeightSmall: '--control-height-small',
    columnWidthDefault: '--column-width-default',
    columnWidthPeopleDefault: '--column-width-people-default',
    columnWidthNameDefault: '--column-width-name-default',
    columnWidthMin: '--column-width-min',
    columnWidthMax: '--column-width-max',
    sidePanelWidth: '--side-panel-width',
    paneWidth: '--pane-width',
    menuWidth: '--menu-width',
    menuWidthGallery: '--menu-width-gallery',
    popoverWidth: '--popover-width',
    modalWidth: '--modal-width',
    bottomBarHeight: '--bottom-bar-height',
    avatarSizeSmall: '--avatar-size-small',
    avatarSizeMedium: '--avatar-size-medium',
    avatarSizeLarge: '--avatar-size-large',
    brandLogoSize: '--brand-logo-size',
    topbarSearchWidth: '--topbar-search-width',
};
/** Colours this package owns (spec §7.3, "Ours"). Vibe's semantic palette is not repeated. */
export const colors = {
    brand: '#6161ff',
    brandHover: '#4f4fcf',
    brandSelected: '#ccccff',
    brandSubtle: '#ebebff',
    paneBorder: '#e7e9ef',
    topbarSearch: '#e8e8ed',
    topbarBorder: '#e3e7f4',
    rowHover: 'rgba(103, 104, 121, 0.1)',
    unreadBackground: '#f0f7ff',
    emptyStatus: '#c4c4c4',
};
export const colorVars = {
    brand: '--brand-color',
    brandHover: '--brand-hover-color',
    brandSelected: '--brand-selected-color',
    brandSubtle: '--brand-subtle-color',
    paneBorder: '--pane-border-color',
    topbarSearch: '--topbar-search-color',
    topbarBorder: '--topbar-border-color',
    rowHover: '--row-hover-color',
    unreadBackground: '--unread-background-color',
    emptyStatus: '--empty-status-color',
};
/**
 * Vibe's light semantic palette, re-stated as literals only so the canvas grid and the tests can
 * use them. Nothing may *declare* these; they are Vibe's and stay Vibe's (spec §7.3).
 */
export const semanticColors = {
    primary: '#0073ea',
    primaryHover: '#0060b9',
    primarySelected: '#cce5ff',
    primaryText: '#323338',
    secondaryText: '#676879',
    primaryBackground: '#ffffff',
    allgreyBackground: '#f6f7fb',
    uiBorder: '#c3c6d4',
    layoutBorder: '#d0d4e4',
    positive: '#00854d',
    negative: '#d83a52',
    warning: '#ffcb00',
    backdrop: 'rgba(41, 47, 76, .7)',
};
/**
 * Z-index layers (spec §7.7). Order observed, values ours.
 *
 * Parity card **P-005** turned this from a list into a contract: one scale, `--ez-layer-*`,
 * and nothing outside `tokens.css` writes a number
 * (`packages/ui/src/tokens/layers.test.ts`). `surface`/`surfaceRaised`/`surfaceTop` are
 * **local** steps for ordering siblings inside one component; the rest are app layers.
 * `scrim` and `drawer` are new: the mobile sidebar used to compute them as
 * `calc(--z-chrome ± n)`, which is how a portalled popover could end up below the drawer.
 */
export const layers = {
    surface: 1,
    surfaceRaised: 2,
    surfaceTop: 3,
    sticky: 10,
    bottomBar: 20,
    chrome: 100,
    scrim: 150,
    drawer: 160,
    panel: 300,
    /** P-261: a phone bottom sheet, above the panels and **below** every menu. */
    sheetBackdrop: 350,
    sheet: 351,
    popover: 500,
    modalBackdrop: 700,
    modal: 701,
    toast: 900,
    tooltip: 1000,
    /** M8-P3: the "view only" bar, above every overlay. Nothing else uses it. */
    impersonation: 1100,
};
export const layerVars = {
    surface: '--ez-layer-surface',
    surfaceRaised: '--ez-layer-surface-raised',
    surfaceTop: '--ez-layer-surface-top',
    sticky: '--ez-layer-sticky',
    bottomBar: '--ez-layer-bottom-bar',
    chrome: '--ez-layer-chrome',
    scrim: '--ez-layer-scrim',
    drawer: '--ez-layer-drawer',
    panel: '--ez-layer-panel',
    sheetBackdrop: '--ez-layer-sheet-backdrop',
    sheet: '--ez-layer-sheet',
    popover: '--ez-layer-popover',
    modalBackdrop: '--ez-layer-modal-backdrop',
    modal: '--ez-layer-modal',
    toast: '--ez-layer-toast',
    tooltip: '--ez-layer-tooltip',
    impersonation: '--ez-layer-impersonation',
};
/** Typography roles (spec §7.2), as the `font:` shorthand each token emits. */
export const fontFamilies = {
    body: "Figtree, Roboto, 'Noto Sans Hebrew', 'Noto Kufi Arabic', 'Noto Sans JP', sans-serif",
    title: "Poppins, Roboto, 'Noto Sans Hebrew', 'Noto Kufi Arabic', 'Noto Sans JP', sans-serif",
};
export const typography = {
    app: { size: 15, weight: 400, lineHeight: 22.5, family: 'body' },
    cell: { size: 14, weight: 400, lineHeight: 20, family: 'body' },
    columnHeader: { size: 14, weight: 400, lineHeight: 20, family: 'body' },
    groupTitle: { size: 18, weight: 500, lineHeight: 24, family: 'title' },
    boardTitle: { size: 24, weight: 500, lineHeight: 32, family: 'title' },
    viewTab: { size: 14, weight: 400, lineHeight: 20, family: 'body' },
    sidebarRow: { size: 14, weight: 400, lineHeight: 20, family: 'body' },
    sidebarRail: { size: 12, weight: 400, lineHeight: 16, family: 'body' },
    paneTitle: { size: 24, weight: 500, lineHeight: 30, family: 'title' },
    pageTitle: { size: 32, weight: 500, lineHeight: 40, family: 'title' },
};
export const typographyVars = {
    app: '--font-app',
    cell: '--font-cell',
    columnHeader: '--font-column-header',
    groupTitle: '--font-group-title',
    boardTitle: '--font-board-title',
    viewTab: '--font-view-tab',
    sidebarRow: '--font-sidebar-row',
    sidebarRail: '--font-sidebar-rail',
    paneTitle: '--font-pane-title',
    pageTitle: '--font-page-title',
};
/** Everything above under one export, for consumers that want a single object. */
export const tokens = {
    dimensions,
    dimensionVars,
    colors,
    colorVars,
    semanticColors,
    layers,
    layerVars,
    typography,
    typographyVars,
    fontFamilies,
};
//# sourceMappingURL=tokens.js.map